import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const content = {
  'zh-TW': {
    category: 'B2B 金融科技與 AI',
    title: 'AI 如何依 UCP 600 預先核對信用證單據，及早發現瑕疵',
    desc: '自動化單據解析如何幫助出口商在交單前發現可能的瑕疵，從而避免部分拒付後隨之而來的銀行費用與延誤。',
    date: '2026 年 8 月',
    s1_title: '拒付的代價：費用與時間',
    s1: '在信用證（LC）交易中，銀行依 UCP 600 與 ISBP 821 (2023) 逐字審單。一個拼寫不一致、一組錯誤的日期、或提單與發票之間的細微出入，都足以構成「單據瑕疵」— 銀行會對遭拒付的交單收取瑕疵費用，而拒付亦可能令付款延誤，直至瑕疵獲解決。而傳統的人手核對，正是這些疏漏的根源。',
    s2_title: 'AI 如何逐條交叉核對 ICC 規則',
    s2: '自動化單據解析引擎會將商業發票、提單、保險單與 SWIFT MT700 開證電文逐欄位結構化，再依 UCP 600 條文與 ISBP 821 (2023) 實務逐項交叉比對 — 從貨物描述一致性、日期邏輯、金額容差到單據簽署要求，通常 30 秒內產出完整的瑕疵風險報告，並標明每項發現對應的規則依據。',
    s3_title: '結果：櫃檯前更少意外',
    s3: '在交單前先做一次預先核對，有助您先行發現並修正可能的瑕疵。這無法保證銀行必定接受交單；LC Shield 提供審核費保證（詳見 getlcshield.com/guarantee）。',
    cta_title: '體驗 AI 單據審核',
    cta_btn: '了解 LC Shield',
    back: '返回洞察中心',
  },
  'zh-CN': {
    category: 'B2B 金融科技与 AI',
    title: 'AI 如何依 UCP 600 预先核对信用证单据，及早发现瑕疵',
    desc: '自动化单据解析如何帮助出口商在交单前发现可能的瑕疵，从而避免部分拒付后随之而来的银行费用与延误。',
    date: '2026 年 8 月',
    s1_title: '拒付的代价：费用与时间',
    s1: '在信用证（LC）交易中，银行依 UCP 600 与 ISBP 821 (2023) 逐字审单。一个拼写不一致、一组错误的日期、或提单与发票之间的细微出入，都足以构成「单据瑕疵」— 银行会对遭拒付的交单收取瑕疵费用，而拒付亦可能令付款延误，直至瑕疵获解决。而传统的人手核对，正是这些疏漏的根源。',
    s2_title: 'AI 如何逐条交叉核对 ICC 规则',
    s2: '自动化单据解析引擎会将商业发票、提单、保险单与 SWIFT MT700 开证电文逐字段结构化，再依 UCP 600 条文与 ISBP 821 (2023) 实务逐项交叉比对 — 从货物描述一致性、日期逻辑、金额容差到单据签署要求，通常 30 秒内产出完整的瑕疵风险报告，并标明每项发现对应的规则依据。',
    s3_title: '结果：柜台前更少意外',
    s3: '在交单前先做一次预先核对，有助您先行发现并修正可能的瑕疵。这无法保证银行必定接受交单；LC Shield 提供审核费保证（详见 getlcshield.com/guarantee）。',
    cta_title: '体验 AI 单据审核',
    cta_btn: '了解 LC Shield',
    back: '返回洞察中心',
  },
  en: {
    category: 'B2B FinTech & AI',
    title: 'How AI Pre-Checks LC Documents Against UCP 600 to Catch Discrepancies Earlier',
    desc: 'How automated document parsing helps exporters spot likely discrepancies before presenting, and avoid some of the bank fees and delays that follow a refusal.',
    date: 'August 2026',
    s1_title: 'A refusal costs fees and time',
    s1: 'In letter of credit transactions, banks examine documents word-by-word against UCP 600 and ISBP 821 (2023). A spelling mismatch, a wrong date, or a subtle inconsistency between the bill of lading and the invoice is enough to constitute a discrepancy — Banks charge discrepancy fees on refused presentations, and a refusal can delay payment while the discrepancy is resolved. Manual checking is precisely where these gaps originate.',
    s2_title: 'How AI cross-checks ICC rules, clause by clause',
    s2: 'An automated parsing engine structures commercial invoices, bills of lading, insurance documents, and the SWIFT MT700 issuance message field-by-field, then cross-checks each item against UCP 600 articles and ISBP 821 (2023) practice — from goods description consistency and date logic to amount tolerances and signing requirements — producing a complete discrepancy risk report typically under 30 seconds, with the governing rule cited for every finding.',
    s3_title: 'The result: fewer surprises at the counter',
    s3: 'Running a pre-check before presentation helps you find and fix likely discrepancies first. It cannot guarantee a bank will accept a presentation; LC Shield offers an Audit Fee Guarantee (see getlcshield.com/guarantee).',
    cta_title: 'Experience AI document auditing',
    cta_btn: 'Discover LC Shield',
    back: 'Back to Insights',
  },
};

export default function InsightUCP600Page() {
  const { lang } = useLanguage();
  const c = content[lang] || content.en;

  return (
    <div data-testid="insight-ucp600-page" className="pt-20 bg-[#080B10]">
      <SEOHead path="/insights/ucp-600" />
      <section className="py-20 md:py-28 bg-[#0E131F]/50 border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="inline-block px-3 py-1 rounded-full bg-[#818CF8]/10 border border-[#818CF8]/30 text-xs font-bold text-[#818CF8] tracking-wide mb-6">
            {c.category}
          </span>
          <h1
            data-testid="insight-title"
            className="text-3xl md:text-4xl lg:text-5xl tracking-tighter font-bold text-white leading-tight mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
          >
            {c.title}
          </h1>
          <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed mb-6" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
            {c.desc}
          </p>
          <p className="text-sm text-white/70">{c.date} · DZent Limited</p>
        </div>
      </section>

      <article className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {[['s1_title', 's1'], ['s2_title', 's2'], ['s3_title', 's3']].map(([ht, bt]) => (
            <section key={ht} className="mb-14">
              <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#F8FAFC] mb-5"
                  style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}>
                {c[ht]}
              </h2>
              <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
                {c[bt]}
              </p>
            </section>
          ))}

          <section className="text-center py-12 border-t border-white/10">
            <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#F8FAFC] mb-6"
                style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}>
              {c.cta_title}
            </h2>
            <Link to="/products/lc-shield">
              <Button data-testid="insight-cta-button"
                      className="h-12 px-8 rounded-full bg-[#6366F1] text-white font-bold text-sm hover:bg-white hover:text-[#080B10] transition-all">
                {c.cta_btn}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </section>

          <div className="pt-8">
            <Link to="/insights" data-testid="insight-back-link"
                  className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-white transition-colors">
              <ChevronLeft className="h-4 w-4" />
              {c.back}
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
